/**
 * AutomationSummary — the DESCRIPTION card in the automation slideout's top
 * zone: `ahoy_summary`, the one-line "what this does".
 *
 * `ahoy_documentation` used to render here too; it moved to its own
 * Documentation tab (automation-documentation.tsx) when the slideout gained a
 * tabbed body — the top zone stays a basic overview, and the long-form prose
 * gets room without a Show-more clamp.
 *
 * `ahoy_summary` is in the type's `hiddenProperties`, so this card is the only
 * place it renders — which is why it diffs when a pending proposal changes it.
 * Renders nothing when empty.
 *
 * Registered on `ENTITY_TYPE_REGISTRY.ahoy_event_automation.components.summary`.
 */

import { usePropertySchema } from '@/lib/crm/hooks/usePropertySchema';
import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

import { useRecordDraft } from '../contexts/RecordDraftContext';
import { MarkdownPropertyBlock } from './markdown-property-block';

export function AutomationSummary({ object }: { object: CrmObject }) {
  const summary = getPropertyValue<string>(object.properties?.ahoy_summary);
  const { schema } = usePropertySchema(object.org_id, object.entity_type_name);
  const draft = useRecordDraft();

  // Presence of the key means a pending proposal changes the property;
  // absence means untouched (buildProjectedObject.ts records nothing for
  // no-op re-sets, so this cannot render an `X → X` diff).
  const previousValues = draft?.previousValues;
  const isSummaryChanged = previousValues
    ? 'ahoy_summary' in previousValues
    : false;

  if (!summary) {
    return null;
  }

  return (
    <MarkdownPropertyBlock
      value={summary}
      previousValue={previousValues?.['ahoy_summary']}
      hasPrevious={isSummaryChanged}
      schema={schema['ahoy_summary']}
      orgId={object.org_id}
      entityProperties={object.properties}
      collapseKey={`${object.id}:summary:${summary}`}
    />
  );
}
